class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    CartDrawer.setChatWidgetsHidden(true);

    setTimeout(() => {
      this.classList.add('animate', 'active');
      CartDrawer.setChatWidgetsHidden(true);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        CartDrawer.setChatWidgetsHidden(true);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    CartDrawer.setChatWidgetsHidden(false);
  }

  static chatWidgetSelector() {
    return [
      '#podium-website-widget',
      '#podium-website-widget *',
      '#podium-widget',
      '[id*="podium"]',
      '[id*="Podium"]',
      '[class*="podium"]',
      '[class*="Podium"]',
      'iframe[src*="podium"]',
      'iframe[src*="connect.podium"]',
      '#ShopifyChat',
      '#shopify-chat',
      'iframe#ShopifyChat',
      'iframe[id*="shopify-chat"]',
      'iframe[id*="ShopifyChat"]',
      'iframe[name*="shopify-chat"]',
      'iframe[name*="ShopifyChat"]',
      '#dummy-chat-button-iframe',
      'iframe[id*="dummy-chat"]',
      'div[data-testid-chat-button]',
      'shopify-chat',
    ].join(', ');
  }

  static chatWidgetNodes() {
    try {
      return document.querySelectorAll(CartDrawer.chatWidgetSelector());
    } catch (error) {
      console.error(error);
      return document.querySelectorAll(
        '#podium-website-widget, #podium-widget, iframe[src*="podium"], #ShopifyChat, #shopify-chat, #dummy-chat-button-iframe, shopify-chat'
      );
    }
  }

  static isCartDrawerOpen() {
    return (
      document.body.classList.contains('cart-drawer-open') ||
      Boolean(document.querySelector('cart-drawer.active'))
    );
  }

  static setChatWidgetsHidden(hidden) {
    const HIDDEN_ATTR = 'data-sleepys-hidden-by-cart-drawer';

    CartDrawer.chatWidgetNodes().forEach((el) => {
      if (hidden) {
        el.setAttribute(HIDDEN_ATTR, 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        el.style.setProperty('z-index', '-1', 'important');
      } else if (el.getAttribute(HIDDEN_ATTR) === 'true') {
        el.removeAttribute(HIDDEN_ATTR);
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
        el.style.removeProperty('z-index');
      }
    });
  }

  /** @deprecated use setChatWidgetsHidden */
  static setPodiumHidden(hidden) {
    CartDrawer.setChatWidgetsHidden(hidden);
  }

  static podiumNodes() {
    return CartDrawer.chatWidgetNodes();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = parsed.querySelector(selector);
    if (el) return el.innerHTML;
    const sectionRoot = parsed.querySelector('.shopify-section');
    return sectionRoot ? sectionRoot.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = selector ? parsed.querySelector(selector) : null;
    if (el) return el.innerHTML;
    const sectionRoot = parsed.querySelector('.shopify-section');
    return sectionRoot ? sectionRoot.innerHTML : parsed.body.innerHTML;
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line: Number(line),
      quantity: Number(quantity),
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const cartDrawerWrapper = document.querySelector('cart-drawer');

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        cartDrawerWrapper?.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections?.[section.section];
          if (!sectionHtml) return;

          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);

          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        });

        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = '';

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });

        if (!cartDrawerWrapper) return;

        if (parsedState.item_count === 0) {
          const emptyEl = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const focusEl =
            emptyEl?.querySelector('a.button') ||
            emptyEl?.querySelector('a') ||
            cartDrawerWrapper.querySelector('.drawer__close');
          if (emptyEl && focusEl) trapFocus(emptyEl, focusEl);
          return;
        }

        const focusEl =
          cartDrawerWrapper.querySelector(`[name="${name}"]`) ||
          cartDrawerWrapper.querySelector('.cart-drawer__item-name') ||
          cartDrawerWrapper.querySelector('.drawer__close');
        const trapContainer = document.getElementById('CartDrawer') || cartDrawerWrapper;
        if (focusEl) trapFocus(trapContainer, focusEl);
      })
      .catch((error) => {
        console.error(error);
        document
          .querySelectorAll('cart-drawer-items .loading__spinner')
          .forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  enableLoading(line) {
    document.getElementById('CartDrawer-CartItems')?.classList.add('cart__items--disabled');
    document
      .querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)
      .forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', 'false');
  }

  disableLoading(line) {
    document.getElementById('CartDrawer-CartItems')?.classList.remove('cart__items--disabled');
    document
      .querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)
      .forEach((overlay) => overlay.classList.add('hidden'));
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message || '';
    }

    this.lineItemStatusElement?.setAttribute('aria-hidden', 'true');

    const cartStatus = document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', 'true');
    }, 1000);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsells extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.loadRecommendations();
  }

  get cartProductIds() {
    return (this.dataset.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  async loadRecommendations() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const baseUrl = this.dataset.url;

    if (!productId || !sectionId || !baseUrl) {
      this.hidden = true;
      return;
    }

    try {
      const separator = baseUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${baseUrl}${separator}product_id=${productId}&section_id=${sectionId}`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const content = html.querySelector('[data-cart-upsells-content]');

      if (!content) {
        this.hidden = true;
        return;
      }

      content.querySelectorAll('[data-product-id]').forEach((card) => {
        if (this.cartProductIds.includes(String(card.dataset.productId))) {
          card.remove();
        }
      });

      if (!content.querySelector('[data-product-id]')) {
        this.hidden = true;
        return;
      }

      this.innerHTML = content.innerHTML;
      this.hidden = false;
      this.dataset.loaded = 'true';
      this.initCarousel();
      this.initAddToCart();
    } catch (error) {
      console.error(error);
      this.hidden = true;
    }
  }

  initCarousel() {
    this.track = this.querySelector('.cart-drawer-upsells__track');
    this.prevButton = this.querySelector('.cart-drawer-upsells__nav--prev');
    this.nextButton = this.querySelector('.cart-drawer-upsells__nav--next');
    this.dotsContainer = this.querySelector('.cart-drawer-upsells__dots');

    if (!this.track) return;

    const cards = [...this.track.querySelectorAll('.cart-drawer-upsells__card')];
    if (cards.length > 1 && this.prevButton && this.nextButton) {
      this.prevButton.hidden = false;
      this.nextButton.hidden = false;
      this.prevButton.addEventListener('click', () => this.scrollByCard(-1));
      this.nextButton.addEventListener('click', () => this.scrollByCard(1));
    }

    if (cards.length > 1 && this.dotsContainer) {
      this.dotsContainer.hidden = false;
      this.dotsContainer.innerHTML = cards
        .map((_, index) => `<button type="button" class="cart-drawer-upsells__dot${index === 0 ? ' is-active' : ''}" aria-label="Go to product ${index + 1}" data-index="${index}"></button>`)
        .join('');

      this.dotsContainer.addEventListener('click', (event) => {
        const dot = event.target.closest('.cart-drawer-upsells__dot');
        if (!dot) return;
        const card = cards[Number(dot.dataset.index)];
        card?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });

      this.track.addEventListener('scroll', () => this.updateDots(cards), { passive: true });
    }
  }

  scrollByCard(direction) {
    if (!this.track) return;
    const card = this.track.querySelector('.cart-drawer-upsells__card');
    if (!card) return;
    const amount = card.getBoundingClientRect().width + 8;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  updateDots(cards) {
    if (!this.dotsContainer || !this.track) return;
    const scrollLeft = this.track.scrollLeft;
    let activeIndex = 0;
    cards.forEach((card, index) => {
      if (card.offsetLeft - this.track.offsetLeft <= scrollLeft + 20) activeIndex = index;
    });
    this.dotsContainer.querySelectorAll('.cart-drawer-upsells__dot').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  initAddToCart() {
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('.cart-drawer-upsells__atc[data-variant-id]');
      if (!button) return;

      event.preventDefault();
      button.disabled = true;

      try {
        const cartDrawer = this.closest('cart-drawer');
        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', '1');

        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        const parsed = await response.json();

        if (parsed.status) {
          console.error(parsed.description || parsed.message);
          button.disabled = false;
          return;
        }

        if (cartDrawer) {
          cartDrawer.renderContents(parsed);
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        button.disabled = false;
      }
    });
  }
}

customElements.define('cart-drawer-upsells', CartDrawerUpsells);

/** Keep chat widgets hidden if they inject while the cart drawer is already open */
(function hideChatWidgetsWhileCartDrawerOpen() {
  if (document.documentElement.dataset.sleepysChatCartHideBound === '1') return;
  document.documentElement.dataset.sleepysChatCartHideBound = '1';

  let pollTimer = null;

  const sync = () => {
    if (typeof CartDrawer === 'undefined') return;
    CartDrawer.setChatWidgetsHidden(CartDrawer.isCartDrawerOpen());
  };

  const startPollingWhileOpen = () => {
    if (pollTimer) return;
    let ticks = 0;
    pollTimer = window.setInterval(() => {
      sync();
      ticks += 1;
      if (!CartDrawer.isCartDrawerOpen() || ticks > 40) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }, 250);
  };

  const onBodyClassChange = () => {
    sync();
    if (CartDrawer.isCartDrawerOpen()) startPollingWhileOpen();
  };

  sync();

  const bodyObserver = new MutationObserver(onBodyClassChange);
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  const rootObserver = new MutationObserver(() => {
    if (CartDrawer.isCartDrawerOpen()) {
      sync();
      startPollingWhileOpen();
    }
  });
  rootObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

